// @ts-nocheck
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { ANNOTATION_TAGS } from './pdfUtils';
import { useTTS } from '../hooks/useTTS';
import { logger } from '../utils/logger';

const IMAGE_PRESET_PROMPTS: string[] = [
  "请详细描述这张图片的内容",
  "分析这张图表/图像中的关键信息",
  "解释这张图片中的概念或原理",
  "这张图片表达了什么观点或结论？",
  "请总结图片中的数据或趋势"
];

interface ImageAnalysisPanelProps {
  show: boolean;
  isSelecting: boolean;
  capturedImage: string | null;
  imageQuestion: string;
  imageAnswer: string;
  imageQuestioning: boolean;
  selectedTag: string;
  onClose: () => void;
  onStartSelection: () => void;
  onCancelSelection: () => void;
  onQuestionChange: (value: string) => void;
  onAnalyze: () => void;
  onTagChange: (value: string) => void;
  onSaveAnnotation: () => void;
  onRetake: () => void;
  [key: string]: any;
}

export default function ImageAnalysisPanel({
  show,
  isSelecting,
  capturedImage,
  imageQuestion,
  imageAnswer,
  imageQuestioning,
  selectedTag,
  onClose,
  onStartSelection,
  onCancelSelection,
  onQuestionChange,
  onAnalyze,
  onTagChange,
  onSaveAnnotation,
  onRetake
}: ImageAnalysisPanelProps) {
  // 独立的 TTS 状态
  const { isGenerating: isGeneratingSpeech, currentAudio, generateSpeech, stopAudio } = useTTS();

  if (!show) return null;

  // 添加调试信息
  logger.log('ImageAnalysisPanel渲染:', {
    show,
    isSelecting,
    capturedImage: !!capturedImage,
    onStartSelection: typeof onStartSelection
  });

  // 处理开始截图的点击事件
  const handleStartSelection = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    logger.log('handleStartSelection被调用');
    logger.log('onStartSelection类型:', typeof onStartSelection);

    if (typeof onStartSelection === 'function') {
      logger.log('调用onStartSelection函数');
      onStartSelection();
    } else {
      console.error('onStartSelection不是函数:', onStartSelection);
    }
  };

  // 处理取消选择的点击事件
  const handleCancelSelection = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    logger.log('取消截图');

    if (typeof onCancelSelection === 'function') {
      onCancelSelection();
    }
  };

  return (
    <div className="image-analysis-panel">
      <div className="image-panel-header">
        <h4>图片分析</h4>
        <button onClick={onClose} className="close-btn">&times;</button>
      </div>

      <div className="image-panel-content">
        {!capturedImage ? (
          <div className="screenshot-section">
            <p className="instruction-text">
              {isSelecting ? '在PDF页面上拖拽选择要分析的区域' : '点击下方按钮开始截图'}
            </p>

            {/* 调试信息显示 */}
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              截图状态: {isSelecting ? '选择中' : '未开始'}
              <br />
              函数状态: {typeof onStartSelection}
            </div>

            <button
              type="button"
              onClick={handleStartSelection}
              className={`screenshot-btn ${isSelecting ? 'active' : ''}`}
              disabled={isSelecting}
              style={{
                pointerEvents: 'auto',
                cursor: isSelecting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSelecting ? '正在选择区域...' : '开始截图'}
            </button>

            {isSelecting && (
              <button
                type="button"
                onClick={handleCancelSelection}
                className="cancel-selection-btn"
                style={{
                  pointerEvents: 'auto',
                  cursor: 'pointer'
                }}
              >
                取消选择
              </button>
            )}
          </div>
        ) : (
          <div className="image-qa-section">
            <div className="captured-image">
              <h5>截图预览:</h5>
              <img src={capturedImage} alt="截图" className="preview-image" />
            </div>

            <div className="image-question-section">
              <label>请提问:</label>
              <div className="preset-prompts">
                <div className="preset-prompts-label">图片分析模板:</div>
                <div className="preset-buttons">
                  {IMAGE_PRESET_PROMPTS.map((prompt, index) => (
                    <button
                      key={index}
                      type="button"
                      className="preset-btn"
                      onClick={() => onQuestionChange(prompt)}
                      title={prompt}
                    >
                      {prompt.length > 15 ? prompt.substring(0, 15) + '...' : prompt}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                value={imageQuestion}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onQuestionChange(e.target.value)}
                placeholder="基于截图内容提问..."
                rows={3}
                className="image-question-input"
              />
              <button
                onClick={onAnalyze}
                disabled={imageQuestioning || !imageQuestion.trim()}
                className="ask-btn"
              >
                {imageQuestioning ? '分析中...' : '分析图片'}
              </button>
            </div>

            {imageAnswer && (
              <div className="image-answer-section">
                <div className="answer-header">
                  <h5>AI分析结果:</h5>
                  <div className="speech-controls">
                    <button
                      onClick={() => generateSpeech(imageAnswer)}
                      disabled={isGeneratingSpeech}
                      className="speech-btn"
                      title="转为语音"
                    >
                      {isGeneratingSpeech ? '生成中...' : '🔊'}
                    </button>
                    {currentAudio && (
                      <button
                        onClick={stopAudio}
                        className="stop-speech-btn"
                        title="停止播放"
                      >
                        ⏹
                      </button>
                    )}
                  </div>
                </div>
                <div className="answer-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {imageAnswer}
                  </ReactMarkdown>
                </div>
                <div className="save-annotation-section">
                  <div className="tag-selection">
                    <label>选择标注类型:</label>
                    <select
                      value={selectedTag}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onTagChange(e.target.value)}
                      className="tag-select"
                    >
                      {Object.entries(ANNOTATION_TAGS).map(([key, config]: [string, any]) => (
                        <option key={key} value={key}>{config.name}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={onSaveAnnotation}
                    className="save-annotation-btn"
                  >
                    保存为标注
                  </button>
                </div>
              </div>
            )}

            <div className="image-actions">
              <button
                onClick={onRetake}
                className="retake-btn"
              >
                重新截图
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
